import { type MaterialPracticeSummary } from '@/platform/materials/learning'

/**
 * Child Learning Session Clarity: every upload makes its own practice set
 * (D-17), so a child who uploads the same worksheet again can already have an
 * unfinished set of that topic. These helpers find such sets and show when each
 * was started. They only read: no set is merged, changed or removed.
 */

/** The server's topic code for fraction addition and subtraction (`fractions.ts`). */
export const FRACTION_TOPIC_CODE = 'fraction_add_sub'

/** A set the learner can still continue (one fully answered but not recorded as done included). */
export function isUnfinished(s: MaterialPracticeSummary): boolean {
  return s.completed_at === null && s.items > 0
}

/** Unfinished sets of the given topic, newest first. */
export function unfinishedSameTopic(
  list: readonly MaterialPracticeSummary[],
  topicCode: string,
): MaterialPracticeSummary[] {
  return list
    .filter((s) => isUnfinished(s) && s.topic_code === topicCode)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
}

/**
 * When a set was started, in the device's time zone: `HH:mm, dd/MM/yyyy`, or
 * `HH:mm:ss, dd/MM/yyyy` with seconds.
 */
export function formatStartedAt(iso: string, seconds = false): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}${
    seconds ? `:${pad(d.getSeconds())}` : ''
  }`
  return `${time}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())}`
}

/**
 * Start times for a list of sets. Seconds are shown only for sets that would
 * otherwise read the same (started in the same minute of the same day), so
 * the rows can be told apart without cluttering the usual case.
 */
export function startedAtLabels(
  sets: readonly Pick<MaterialPracticeSummary, 'created_at'>[],
): (set: Pick<MaterialPracticeSummary, 'created_at'>) => string {
  const perMinute = new Map<string, number>()
  for (const s of sets) {
    const minute = formatStartedAt(s.created_at)
    perMinute.set(minute, (perMinute.get(minute) ?? 0) + 1)
  }
  return (set) =>
    formatStartedAt(
      set.created_at,
      (perMinute.get(formatStartedAt(set.created_at)) ?? 0) > 1,
    )
}
